// Pure helpers for WMS GetFeatureInfo popups. No network, no ArcGIS SDK: easy to test from Node.
export const DEFAULT_WMS_VERSION = '1.3.0';
export const DEFAULT_FEATURE_COUNT = 5;
export const DEFAULT_CLICK_BUFFER_PX = 8;
// Web Mercator has several historical wkids that all map to EPSG:3857.
const WEB_MERCATOR_WKIDS = [102100, 102113, 3857];
// CRSs whose WMS 1.3.0 axis order is latitude,longitude (BBOX = ymin,xmin,ymax,xmax).
const LAT_LON_AXIS_ORDER_EPSG = [4326, 4258, 4269];
export function resolveEpsgCode(wkid) {
    return WEB_MERCATOR_WKIDS.includes(wkid) ? 3857 : wkid;
}
function isLatLonAxisOrder(version, epsg) {
    return version === '1.3.0' && LAT_LON_AXIS_ORDER_EPSG.includes(epsg);
}
export function buildBbox(extent, version, epsg) {
    const { xmin, ymin, xmax, ymax } = extent;
    const values = isLatLonAxisOrder(version, epsg) ? [ymin, xmin, ymax, xmax] : [xmin, ymin, xmax, ymax];
    return values.join(',');
}
export function buildGetFeatureInfoUrl(params) {
    const version = params.version || DEFAULT_WMS_VERSION;
    const epsg = resolveEpsgCode(params.wkid);
    const isLegacy = version === '1.1.1' || version === '1.1.0';
    const layers = params.layers.join(',');
    const url = new URL(params.baseUrl);
    const query = url.searchParams;
    query.set('service', 'WMS');
    query.set('version', version);
    query.set('request', 'GetFeatureInfo');
    query.set('layers', layers);
    query.set('query_layers', layers);
    query.set('styles', '');
    query.set(isLegacy ? 'srs' : 'crs', `EPSG:${epsg}`);
    query.set('bbox', buildBbox(params.extent, version, epsg));
    query.set('width', String(Math.round(params.width)));
    query.set('height', String(Math.round(params.height)));
    query.set(isLegacy ? 'x' : 'i', String(Math.round(params.x)));
    query.set(isLegacy ? 'y' : 'j', String(Math.round(params.y)));
    query.set('info_format', 'application/json');
    query.set('feature_count', String(params.featureCount ?? DEFAULT_FEATURE_COUNT));
    query.set('buffer', String(params.buffer ?? DEFAULT_CLICK_BUFFER_PX));
    return url.toString();
}
/** Extracts features from a GeoServer GeoJSON FeatureCollection; anything else yields an empty list. */
export function parseFeatureInfoResponse(body) {
    const features = body?.features;
    if (!Array.isArray(features))
        return [];
    return features
        .filter((feature) => feature && typeof feature.properties === 'object' && feature.properties !== null)
        .map((feature) => ({
        properties: feature.properties,
        geometryType: typeof feature.geometry?.type === 'string' ? feature.geometry.type : null,
        geometry: feature.geometry?.type ? feature.geometry : null,
    }));
}
export function isPointGeometryType(geometryType) {
    return geometryType === 'Point' || geometryType === 'MultiPoint';
}
// Drops any z/m ordinate: highlights are drawn on the ground.
function toXY(position) {
    return [position[0], position[1]];
}
const toXYList = (positions) => positions.map(toXY);
/**
 * Converts a GeoJSON geometry into ArcGIS JSON. Coordinates are copied as-is: GeoServer already returns them in the
 * request CRS, so `wkid` must be the same one used to build the GetFeatureInfo request (the view's spatial reference).
 * Returns null for missing or unsupported geometry types.
 */
export function geoJsonToEsriJson(geometry, wkid) {
    if (!geometry || !Array.isArray(geometry.coordinates))
        return null;
    const spatialReference = { wkid };
    const { coordinates } = geometry;
    switch (geometry.type) {
        case 'Point': {
            const [x, y] = toXY(coordinates);
            return { x, y, spatialReference };
        }
        case 'MultiPoint':
            return { points: toXYList(coordinates), spatialReference };
        case 'LineString':
            return { paths: [toXYList(coordinates)], spatialReference };
        case 'MultiLineString':
            return { paths: coordinates.map(toXYList), spatialReference };
        case 'Polygon':
            return { rings: coordinates.map(toXYList), spatialReference };
        case 'MultiPolygon':
            return { rings: coordinates.flatMap((polygon) => polygon.map(toXYList)), spatialReference };
        default:
            return null;
    }
}
